import React, { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { DOCS, DOC_CATEGORIES } from "@/data/docs";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowRight, Info, CheckCircle2, AlertTriangle, Lightbulb, Lock } from "lucide-react";
import AppShell from "@/components/app/AppShell";
import DuckyAvatar from "@/components/app/DuckyAvatar";

const CALLOUT = {
  info:    { icon: Info,          cls: "bg-sky-50 border-sky-200 text-sky-900" },
  success: { icon: CheckCircle2,  cls: "bg-emerald-50 border-emerald-200 text-emerald-900" },
  warn:    { icon: AlertTriangle, cls: "bg-amber-50 border-amber-200 text-amber-900" },
};

export default function DocPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const doc = useMemo(() => DOCS.find((d) => d.slug === slug), [slug]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="font-display text-3xl font-black text-slate-900">Doc not found</p>
          <Link to="/docs" className="text-amber-600 font-bold mt-4 inline-block">Back to Help Center →</Link>
        </div>
      </div>
    );
  }

  // Gated docs — advanced pages require login
  if (doc.gated && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md">
          <Lock size={40} className="mx-auto text-slate-400" />
          <p className="font-display text-2xl font-black text-slate-900 mt-4">This guide is client-only</p>
          <p className="text-slate-600 mt-2">Sign in to your client portal to view it.</p>
          <Link to="/login" className="inline-flex items-center gap-2 mt-6 bg-slate-900 hover:bg-slate-800 text-white h-12 px-6 rounded-2xl font-black transition">Log in</Link>
        </div>
      </div>
    );
  }

  const category = DOC_CATEGORIES.find((c) => c.slug === doc.category);
  const related = (doc.related || []).map((s) => DOCS.find((d) => d.slug === s)).filter(Boolean);

  const body = (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      {!user && (
        <header className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/docs" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="docs-back-btn">
              <ArrowLeft size={15} /> Help Center
            </Link>
            <p className="text-xs text-slate-400 font-semibold hidden sm:block">Updated {doc.updated}</p>
          </div>
        </header>
      )}
      {user && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Link to="/docs" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="docs-back-btn">
            <ArrowLeft size={15} /> Help Center
          </Link>
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="cq-fade-up">
          {category && (
            <Link to={`/docs?cat=${category.slug}`} className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 hover:text-amber-700">
              {category.name}
            </Link>
          )}
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-3 leading-tight inline-flex items-center gap-3 flex-wrap" data-testid={`doc-title-${doc.slug}`}>
            {doc.hero?.emoji === "🦆"
              ? <DuckyAvatar size={48} />
              : doc.hero?.emoji && <span>{doc.hero.emoji}</span>}
            <span>{doc.title}</span>
          </h1>
          <p className="text-slate-600 text-lg mt-3">{doc.subtitle}</p>
        </div>

        <div className="mt-10 space-y-10">
          {doc.sections.map((s, i) => (
            <section key={i} className="cq-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              {s.heading && <h2 className="font-display text-2xl font-black text-slate-900">{s.heading}</h2>}
              {s.body && <p className="text-slate-700 mt-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderInline(s.body) }} />}
              {s.bullets && (
                <ul className="mt-4 space-y-2.5">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex gap-2.5 text-slate-700 leading-relaxed">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span dangerouslySetInnerHTML={{ __html: renderInline(b) }} />
                    </li>
                  ))}
                </ul>
              )}
              {s.callout && <Callout {...s.callout} />}
              {s.tip && (
                <div className="mt-4 flex gap-3 p-4 rounded-2xl bg-white border border-slate-100 shadow-sm">
                  <Lightbulb size={18} className="text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700"><span className="font-bold">Tip.</span> {s.tip}</p>
                </div>
              )}
            </section>
          ))}
        </div>

        {related.length > 0 && (
          <div className="mt-16 pt-10 border-t border-slate-200">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-4">Related</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/docs/${r.slug}`} className="p-4 rounded-2xl bg-white border border-slate-100 hover:border-amber-200 hover:shadow-md transition group flex items-center justify-between" data-testid={`related-${r.slug}`}>
                  <div>
                    <p className="font-display font-bold text-slate-900 group-hover:text-amber-600 transition">{r.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{r.subtitle}</p>
                  </div>
                  <ArrowRight size={16} className="text-slate-400 group-hover:text-amber-500 group-hover:translate-x-0.5 transition" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );

  return user ? <AppShell>{body}</AppShell> : body;
}

function Callout({ tone = "info", text }) {
  const cfg = CALLOUT[tone] || CALLOUT.info;
  const Icon = cfg.icon;
  return (
    <div className={`mt-4 flex gap-3 p-4 rounded-2xl border ${cfg.cls}`}>
      <Icon size={18} className="shrink-0 mt-0.5" />
      <p className="text-sm leading-relaxed">{text}</p>
    </div>
  );
}

// Very small inline renderer: **bold** and `code`
function renderInline(txt = "") {
  return txt
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-slate-900">$1</strong>')
    .replace(/`([^`]+?)`/g, '<code class="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[0.9em] font-mono text-slate-800">$1</code>');
}
